import { createFileRoute } from "@tanstack/react-router";
import { byCategory } from "@/data/products";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ProductGrid } from "@/components/site/product-grid";
import { MaterialExplorer } from "@/components/site/material-explorer";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/lathe-turned-gifts")({
  head: () => ({
    meta: [
      { title: "Lathe-Turned Gifts | Bottle Stoppers & Duck Calls | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Handmade bottle stoppers, duck calls and turned keepsakes, personalized and made to order in Paragould, Arkansas.",
      },
      { property: "og:title", content: "Handmade bottle stoppers, duck calls and turned gifts" },
      {
        property: "og:description",
        content: "Lathe-turned gifts personalized with your materials, names and dates.",
      },
      { property: "og:url", content: pageUrl("/lathe-turned-gifts") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/lathe-turned-gifts") }],
  }),
  component: LatheGifts,
});

const groups = [
  {
    id: "bottle-stoppers",
    title: "Bottle stoppers",
    plain: "For the person who opens the wine",
    body: "A turned handle over a food-safe stainless base. Small, useful and easy to personalize for a wedding, a housewarming or a thank-you.",
  },
  {
    id: "duck-calls",
    title: "Duck calls",
    plain: "For the person in the blind before sunrise",
    body: "Turned, tuned and finished by hand in dense woods that hold detail. Names and dates can be added to the barrel.",
  },
  {
    id: "fidget-spinners",
    title: "Fidget spinners",
    plain: "For restless hands",
    body: "Balanced wooden or resin bodies with a smooth bearing and a hand-rubbed finish.",
  },
  {
    id: "other",
    title: "Other turned pieces",
    plain: "For everything else",
    body: "Keepsake boxes, handles, ornaments and one-off projects. If it can be held between centers, Paul can probably turn it.",
  },
];

const occasions = [
  "Retirement",
  "Anniversary",
  "Housewarming",
  "Graduation",
  "Holiday",
  "Recognition",
  "Memorial",
  "Just because",
];

function LatheGifts() {
  const items = byCategory("lathe-gifts");
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Lathe-Turned Gifts" }]} />
      <PageHeader
        eyebrow="Lathe-turned gifts"
        title="Gifts that were shaped, not ordered."
        lede="Bottle stoppers, duck calls and turned keepsakes. Small pieces, made one at a time, that carry a name or a date without shouting about it."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="groups-heading">
        <div className="shell">
          <SectionHeading id="groups-heading" eyebrow="What Paul turns" title="Start with the person." />
          <dl className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2">
            {groups.map((g) => (
              <div key={g.id} id={g.id} className="bg-background p-7">
                <dt>
                  <span className="eyebrow text-[0.65rem]">{g.plain}</span>
                  <span className="mt-2 block font-[family-name:var(--font-display)] text-2xl">{g.title}</span>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{g.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <Section labelledBy="lathe-items-heading">
        <div className="shell">
          <SectionHeading
            id="lathe-items-heading"
            eyebrow="Featured creations"
            title="On and off the bench."
            lede="Anything marked Previously Created can be remade in your own choice of wood or resin."
          />
          <ProductGrid products={items} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="occasion-heading">
        <div className="shell grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <SectionHeading
              id="occasion-heading"
              eyebrow="Occasions"
              title="Where these tend to land."
              lede="Turned gifts suit the moments where a card is not quite enough and something extravagant would be too much."
            />
            <ul className="mt-8 flex flex-wrap gap-2">
              {occasions.map((o) => (
                <li
                  key={o}
                  className="rounded-full border border-ink/15 bg-background px-4 py-2 text-sm text-ink/80"
                >
                  {o}
                </li>
              ))}
            </ul>
          </div>
          <div className="surface-card p-8">
            <h2 className="display-md">What can be personalized</h2>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <li>The wood or resin, including material you supply yourself where it is safe to turn.</li>
              <li>The profile — taller, rounder, plainer, more detailed.</li>
              <li>A name, a date or a short message, engraved where there is room.</li>
              <li>The finish, from a soft satin to a full polish.</li>
            </ul>
            <p className="mt-5 text-xs text-muted-foreground">
              Some woods will not hold fine engraving. Paul will tell you before you commit.
            </p>
            <div className="mt-7">
              <CtaLink to="/design-your-piece" arrow className="h-12">
                Start a request
              </CtaLink>
            </div>
          </div>
        </div>
      </Section>

      <Section labelledBy="lathe-materials-heading">
        <div className="shell">
          <SectionHeading
            id="lathe-materials-heading"
            eyebrow="Materials"
            title="What it can be made from."
          />
          <MaterialExplorer />
        </div>
      </Section>

      <FinalCTA heading="Describe the person, and Paul will suggest the piece." />
    </>
  );
}
